import React, { useState, useEffect } from 'react';

import { Box, Typography, RadioGroup, FormControlLabel, Radio, CircularProgress, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';

import QualityObservationScoreService from '../../services/quality-observation-score.service';

const useStyles = makeStyles(() => ({
  buttonContainer: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px',
  },
  radio: {
    padding: '0 8px',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
  },
  radioLabel: {
    '& svg': {
      fill: '#2AAAE2',
    },
    display: 'flex',
    flexDirection: 'column-reverse',
    fontSize: 14,
    margin: 0,
  },
  saveButton: {
    width: '120px',
  },
  scoreContainer: {
    backgroundColor: '#F4F4F4',
    borderRadius: 5,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    marginBottom: 28,
    padding: '30px 26px 30px 40px',
    textAlign: 'left',
  },
  scoreEndLabel: {
    color: '#A5A5A5',
    fontSize: 12,
    lineHeight: '20px',
  },
  scoreLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
    width: 160,
  },
  scoreRow: {
    alignItems: 'end',
    display: 'grid',
    gridTemplateColumns: '80px 380px auto',
  },
  scoreTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 20,
  },
  sendButton: {
    width: '213px',
  },
}));

const QualityObservationScoring = ({ observation, isCompleted, updateStatus }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [scores, setScores] = useState({
    educationalValue: null,
    frequencyOfIssue: null,
    severityOfIssue: null,
  });
  const [saving, setSaving] = useState(false);
  const [sendingToTechnologist, setSendingToTechnologist] = useState(false);
  const [loadingScores, setLoadingScores] = useState(false);
  const [scoresSaved, setScoresSaved] = useState(false);

  const observationId = observation.id;

  useEffect(() => {
    const fetchScores = async () => {
      if (!observationId) return;

      try {
        setLoadingScores(true);

        const data = await QualityObservationScoreService.getScore(observationId);

        setScores({
          educationalValue: data?.score?.educationalValue || null,
          frequencyOfIssue: data?.score?.frequencyOfIssue || null,
          severityOfIssue: data?.score?.severityOfIssue || null,
        });

        if (data?.score) {
          setScoresSaved(true);
        }
      } catch (err) {
        console.error('Error fetching scores:', err);
        enqueueSnackbar('Error fetching scores', { variant: 'error' });
      } finally {
        setLoadingScores(false);
      }
    };

    fetchScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observationId]);

  const handleChange = (field) => (event) => {
    setScores({
      ...scores,
      [field]: parseInt(event.target.value, 10),
    });
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const updatedScores = await QualityObservationScoreService.saveScore(observationId, scores);

      setScores({
        educationalValue: updatedScores?.educationalValue || null,
        frequencyOfIssue: updatedScores?.frequencyOfIssue || null,
        severityOfIssue: updatedScores?.severityOfIssue || null,
      });

      setScoresSaved(true);
      enqueueSnackbar('Scores saved successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error saving scores:', err);
      enqueueSnackbar('Error saving scores', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendToTechnologist = async () => {
    setSendingToTechnologist(true);
    try {
      await QualityObservationScoreService.sendScoreToTechnologist(observationId);

      enqueueSnackbar('Scores sent to technologist successfully', { variant: 'success' });
      updateStatus();
    } catch (err) {
      console.error('Error sending scores to technologist:', err);
      enqueueSnackbar('Error sending scores to technologist', { variant: 'error' });
    } finally {
      setSendingToTechnologist(false);
    }
  };

  const areAllScoresSelected = () => {
    return scores.educationalValue !== null && scores.frequencyOfIssue !== null && scores.severityOfIssue !== null;
  };

  const renderRadioOptions = (field, label, leftLabel, rightLabel) => (
    <Box mb={3}>
      <Typography className={classes.scoreLabel}>{label}</Typography>

      <div className={classes.scoreRow}>
        <Typography className={classes.scoreEndLabel}>{leftLabel}</Typography>

        <div className={classes.scoreNumbers}>
          <RadioGroup row name={field} value={scores[field] === null ? '' : scores[field].toString()} onChange={handleChange(field)} className={classes.radioGroup}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
              <FormControlLabel
                key={value}
                value={value.toString()}
                control={<Radio className={classes.radio} color="primary" size="small" disabled={isCompleted} />}
                label={value}
                className={classes.radioLabel}
                disabled={isCompleted}
              />
            ))}
          </RadioGroup>
        </div>

        <span className={classes.scoreEndLabel}>{rightLabel}</span>
      </div>
    </Box>
  );

  return (
    <Box display="flex" flexDirection="column">
      {loadingScores ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box>
          <Box className={classes.scoreContainer}>
            <div className={classes.scoreTitle}>Score Material</div>

            <Box display="flex" flexDirection="column" gridGap="20px">
              {renderRadioOptions('educationalValue', 'Educational value', 'Not valuable', 'Extremely valuable')}

              {renderRadioOptions('severityOfIssue', 'Severity of issue', 'Not severe', 'Critical issue')}

              {renderRadioOptions('frequencyOfIssue', 'Frequency of issue', 'Not frequent', 'Extremely Frequent')}
            </Box>
          </Box>
        </Box>
      )}

      {!isCompleted && !loadingScores && (
        <Box className={classes.buttonContainer}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !areAllScoresSelected()} className={classes.saveButton}>
            Save
          </Button>

          {scoresSaved && (
            <Button variant="outlined" color="primary" onClick={handleSendToTechnologist} disabled={sendingToTechnologist} className={classes.sendButton}>
              {observation.emailSent ? 'Resend Email' : 'Send to Technologist'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default QualityObservationScoring;
